import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, Search, Trash2, Trophy, TrendingUp, Calendar, RefreshCw, Key } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminUsersPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !user.is_admin) {
      toast.error('אין לך הרשאות אדמין');
      navigate('/');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(u => 
        u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      console.log('Fetching users with token:', token ? 'Token exists' : 'No token');
      
      if (!token) {
        toast.error('נדרש התחברות כאדמין');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Users loaded:', response.data.users.length);
      setUsers(response.data.users);
      setFilteredUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        toast.error('אין הרשאות אדמין - אנא התחבר כאדמין');
      } else {
        toast.error('שגיאה בטעינת משתמשים: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTop7GradeA = async () => {
    const gradeAUsers = users
      .filter(u => u.skill_level === 'דרג א')
      .sort((a, b) => (b.total_points || 0) - (a.total_points || 0))
      .slice(0, 7);
    
    if (gradeAUsers.length === 0) {
      toast.error('אין משתמשים בדרג א');
      return;
    }
    
    const userNames = gradeAUsers.map(u => u.full_name).join('\n• ');
    const confirmMessage = `⚠️ אזהרה: פעולה זו תמחק את TOP ${gradeAUsers.length} משתמשים בדרג א:

• ${userNames}

כל הנתונים שלהם (הודעות, רישומים, מעקבים) יימחקו לצמיתות!

הקלד "מחק הכל" כדי לאשר:`;
    
    const userInput = prompt(confirmMessage);
    
    if (userInput !== 'מחק הכל') {
      toast.info('המחיקה בוטלה');
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      let deletedCount = 0;
      
      for (const user of gradeAUsers) {
        if (!user.is_admin) {
          await axios.delete(`${API}/api/admin/users/${user.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          deletedCount++;
        }
      }
      
      toast.success(`נמחקו ${deletedCount} משתמשים בהצלחה`);
      fetchUsers();
    } catch (error) {
      toast.error('שגיאה במחיקה המונית');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    const confirmMessage = `⚠️ אזהרה: פעולה זו תמחק לצמיתות את:

• המשתמש: ${userName}
• כל ההודעות שלו
• כל הרישומים שלו לטורנירים
• כל המעקבים (followers/following)

האם אתה בטוח לחלוטין שברצונך למחוק משתמש זה?

הקלד "מחק" כדי לאשר:`;
    
    const userInput = prompt(confirmMessage);
    
    if (userInput !== 'מחק') {
      toast.info('המחיקה בוטלה');
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.delete(`${API}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userName} נמחק בהצלחה מהמערכת`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה במחיקת משתמש');
    }
  };

  const handleResetPassword = async (userId, userName, userEmail) => {
    const newPassword = prompt(`איפוס סיסמה עבור: ${userName}\n\nהזן סיסמה חדשה (לפחות 6 תווים):`);
    
    if (!newPassword) {
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('הסיסמה חייבת להכיל לפחות 6 תווים');
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(`${API}/api/admin/reset-user-password`, {
        user_id: userId,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`הסיסמה אופסה בהצלחה!\n\nאימייל: ${userEmail}\nסיסמה חדשה: ${newPassword}`, { duration: 10000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה באיפוס הסיסמה');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">טוען...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                👥 ניהול משתמשים
              </h1>
              <p className="text-gray-600">סה"כ {users.length} משתמשים רשומים</p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={fetchUsers}
                className="flex items-center gap-2"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                רענן
              </Button>
              
              <Button
                variant="destructive"
                onClick={handleDeleteTop7GradeA}
                className="flex items-center gap-2"
                disabled={loading}
              >
                <Trash2 className="w-4 h-4" />
                מחק TOP 7 דרג א
              </Button>
            </div>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
            <strong>⚠️ שים לב:</strong> מחיקת משתמש תמחק גם את כל ההודעות, הרישומים לטורנירים והמעקבים שלו.
          </div>
        </div>

        {/* Search */}
        <Card className="mb-6 glass-effect">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="חפש לפי שם או אימייל..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        <div className="grid grid-cols-1 gap-4">
          {filteredUsers.length === 0 ? (
            <Card className="glass-effect">
              <CardContent className="py-12 text-center">
                {users.length === 0 ? (
                  <div>
                    <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600 text-lg mb-2">אין משתמשים במערכת</p>
                    <p className="text-gray-500 text-sm">משתמשים חדשים שירשמו יופיעו כאן</p>
                  </div>
                ) : (
                  <div>
                    <Search className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600">לא נמצאו משתמשים התואמים את החיפוש "{searchTerm}"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredUsers.map((usr) => (
              <Card key={usr.id} className="glass-effect hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <Avatar className="w-16 h-16">
                        {usr.avatar && <AvatarImage src={usr.avatar} alt={usr.full_name} />}
                        <AvatarFallback className="bg-blue-600 text-white text-xl">
                          {usr.full_name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-xl font-bold text-gray-900">
                            {usr.full_name}
                          </h3>
                          {usr.is_admin && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-bold rounded">
                              ADMIN
                            </span>
                          )}
                        </div>
                        
                        <p className="text-gray-600 mb-3">{usr.email}</p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Trophy className="w-4 h-4 text-blue-600" />
                            <span>{usr.skill_level || 'לא צוין'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-green-600" />
                            <span>{usr.total_points || 0} נקודות</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Trophy className="w-4 h-4 text-yellow-600" />
                            <span>{usr.total_wins || 0} ניצחונות</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-purple-600" />
                            <span>{usr.total_tournaments || 0} תחרויות</span>
                          </div>
                        </div>

                        {usr.phone && (() => {
                          const cleanPhone = usr.phone.replace(/[-\s()]/g, '');
                          const intlPhone = cleanPhone.startsWith('0') ? '972' + cleanPhone.slice(1) : cleanPhone.startsWith('+') ? cleanPhone.slice(1) : cleanPhone;
                          const firstName = (usr.full_name || '').split(' ')[0] || '';
                          const text = encodeURIComponent(`היי ${firstName}, מדבר/ת מ-Tennis Pro Israel 🎾`);
                          return (
                            <a
                              href={`https://wa.me/${intlPhone}?text=${text}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`admin-wa-list-${usr.id}`}
                              className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-800 hover:underline mt-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span>📱</span><span dir="ltr">{usr.phone}</span>
                            </a>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Link to={`/profile/${usr.id}`}>
                        <Button variant="outline" size="sm">
                          צפה בפרופיל
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResetPassword(usr.id, usr.full_name, usr.email)}
                        className="text-blue-600 hover:bg-blue-50"
                        title="איפוס סיסמה"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      {!usr.is_admin && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteUser(usr.id, usr.full_name)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;
